import './QuestionsSection.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  loadQuestionsData, saveQuestion, toggleQuestion, removeQuestion, uploadImage, EMPTY_QUESTION,
  readAndParseCSV, saveBulkQuestions, downloadCSVTemplate, describeCsvRowValidationError,
} from '../../logic/questionsLogic';
import { ActIcon } from '../../components/AdminIcons';

const IMG_BASE   = process.env.REACT_APP_IMG_BASE;
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];
const OPTION_COLORS  = { A: '#6366f1', B: '#8b5cf6', C: '#06b6d4', D: '#f59e0b' };
const PAGE_SIZE  = 20;

export default function QuestionsSection({ notify, can }) {
  const [view,        setView]        = useState('list');
  const [questions,   setQuestions]   = useState([]);
  const [qStats,      setQStats]      = useState(null);
  const [categories,  setCategories]  = useState([]);
  const [qFilter,     setQFilter]     = useState({ category: '', difficulty: '' });
  const [loading,     setLoading]     = useState(false);
  const [busy,        setBusy]        = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [qForm,   setQForm]   = useState({ ...EMPTY_QUESTION, time_limit: 60 });
  const [tags,    setTags]    = useState([]);
  const [tagInput,setTagInput]= useState('');
  const [qEditId, setQEditId] = useState(null);

  // CSV import state
  const [csvRows,      setCsvRows]      = useState([]);
  const [csvDragOver,  setCsvDragOver]  = useState(false);
  const csvInputRef = useRef(null);

  const isBusy  = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { questions, stats, categories } = await loadQuestionsData(qFilter);
      setQuestions(questions); setQStats(stats); setCategories(categories);
      setCurrentPage(1);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load questions', true); }
    finally { setLoading(false); }
  }, [notify, qFilter]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (q = null) => {
    if (q) {
      setQEditId(q.id);
      setQForm({
        question_text: q.question_text,
        option_a: q.option_a,         option_b: q.option_b,
        option_c: q.option_c,         option_d: q.option_d,
        correct_option: q.correct_option,
        difficulty: q.difficulty,     category: q.category || '',
        marks: q.marks,               time_limit: q.time_limit || 60,
        question_image:   q.question_image   || '',
        option_a_image:   q.option_a_image   || '',
        option_b_image:   q.option_b_image   || '',
        option_c_image:   q.option_c_image   || '',
        option_d_image:   q.option_d_image   || '',
      });
      setTags(q.tags || []);
    } else {
      setQEditId(null);
      setQForm({ ...EMPTY_QUESTION, time_limit: 60 });
      setTags([]);
    }
    setView('editor');
  };

  const backToList = () => {
    setView('list');
    setQEditId(null);
    setQForm({ ...EMPTY_QUESTION, time_limit: 60 });
    setTags([]);
  };

  const uploadQuestionImage = async (file, field) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setQForm(f => ({ ...f, [field]: url }));
      notify('Image uploaded');
    } catch (err) { notify(err.response?.data?.message || 'Image upload failed', true); }
  };

  const handleSave = async (isDraft = false) => {
    if (!qForm.question_text.trim()) { notify('Question text is required', true); return; }
    await withBusy('save-q', async () => {
      try {
        const payload = { ...qForm, tags };
        const action  = await saveQuestion(payload, qEditId);
        notify(action === 'updated' ? 'Question updated' : isDraft ? 'Draft saved' : 'Question published');
        if (!isDraft) { backToList(); load(); }
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const handleToggle = async (id, current) => {
    await withBusy(`qtgl-${id}`, async () => {
      try {
        await toggleQuestion(id, current);
        notify(`Question ${!current ? 'activated' : 'deactivated'}`); load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question permanently?')) return;
    await withBusy(`qdel-${id}`, async () => {
      try { await removeQuestion(id); notify('Question deleted'); load(); }
      catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const addTag = () => {
    const t = tagInput.trim().toUpperCase();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };

  /* ── CSV import handlers ──────────────────────────────────────────────────── */
  const openCSVImport = () => { setCsvRows([]); setView('csv-import'); };

  const handleCSVFile = async (file) => {
    if (!file) return;
    try {
      const rows = await readAndParseCSV(file);
      setCsvRows(rows);
      notify(`${rows.length} question(s) parsed — review & edit below`);
    } catch (err) {
      notify(err.message || 'Failed to parse CSV', true);
    }
  };

  const handleCSVDrop = (e) => {
    e.preventDefault(); setCsvDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCSVFile(file);
  };

  const updateCsvRow = (idx, field, value) => {
    setCsvRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeCsvRow = (idx) => {
    setCsvRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBulkSave = async () => {
    if (csvRows.length === 0) { notify('No questions to save', true); return; }
    const invalid = csvRows.findIndex(r => describeCsvRowValidationError(r));
    if (invalid !== -1) {
      const detail = describeCsvRowValidationError(csvRows[invalid]);
      notify(`Row ${invalid + 1}: ${detail}`, true);
      return;
    }
    await withBusy('csv-save', async () => {
      try {
        const count = await saveBulkQuestions(csvRows);
        notify(`${count} question(s) imported successfully`);
        setView('list'); load();
      } catch (err) {
        notify(err.response?.data?.message || 'Bulk import failed', true);
      }
    });
  };

  /* ══════════════════════════════════════════════════════
     CSV IMPORT VIEW
  ══════════════════════════════════════════════════════ */
  if (view === 'csv-import') {
    return (
      <div className="cq-container fade-up">

        {/* Top bar */}
        <div className="cq-topbar">
          <div className="cq-breadcrumb">
            <button className="cq-breadcrumb-link" onClick={() => setView('list')}>Question Bank</button>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            <span className="cq-breadcrumb-cur">Import via CSV</span>
          </div>
          <div className="cq-header-row">
            <h2 className="cq-title">CSV Bulk Import</h2>
            <div className="cq-header-actions">
              <button className="btn btn-outline btn-sm" onClick={downloadCSVTemplate}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download Template
              </button>
              {csvRows.length > 0 && (
                <button className="btn btn-primary btn-sm" disabled={isBusy('csv-save')} onClick={handleBulkSave}>
                  {isBusy('csv-save') ? <><span className="btn-spinner"/>Saving…</> : `Save ${csvRows.length} Question${csvRows.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Upload zone (shown when no rows parsed yet) */}
        {csvRows.length === 0 && (
          <div
            className={`csv-drop-zone${csvDragOver ? ' csv-drop-zone--over' : ''}`}
            onDragOver={e => { e.preventDefault(); setCsvDragOver(true); }}
            onDragLeave={() => setCsvDragOver(false)}
            onDrop={handleCSVDrop}
            onClick={() => csvInputRef.current?.click()}
          >
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => handleCSVFile(e.target.files[0])}
            />
            <div className="csv-drop-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
            </div>
            <p className="csv-drop-title">Drag &amp; drop a CSV file here</p>
            <p className="csv-drop-sub">or <span className="csv-drop-link">click to browse</span></p>
            <div className="csv-format-hint">
              <strong>Required columns:</strong> question_text, option_a, option_b, option_c, option_d, correct_option<br/>
              <strong>Optional:</strong> difficulty (easy/medium/hard), category, marks, time_limit
            </div>
          </div>
        )}

        {/* Preview + edit table */}
        {csvRows.length > 0 && (
          <>
            <div className="csv-preview-header">
              <span className="csv-preview-count">{csvRows.length} question{csvRows.length !== 1 ? 's' : ''} ready to import</span>
              <button className="btn btn-outline btn-sm" onClick={() => { setCsvRows([]); csvInputRef.current && (csvInputRef.current.value = ''); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                Re-upload
              </button>
            </div>

            <div className="csv-preview-scroll">
              <table className="csv-preview-table">
                <thead>
                  <tr>
                    <th style={{width:'2.5rem'}}>#</th>
                    <th style={{minWidth:'220px'}}>Question Text</th>
                    <th style={{minWidth:'130px'}}>Option A</th>
                    <th style={{minWidth:'130px'}}>Option B</th>
                    <th style={{minWidth:'130px'}}>Option C</th>
                    <th style={{minWidth:'130px'}}>Option D</th>
                    <th style={{width:'80px'}}>Correct</th>
                    <th style={{width:'100px'}}>Difficulty</th>
                    <th style={{minWidth:'110px'}}>Category</th>
                    <th style={{width:'60px'}}>Marks</th>
                    <th style={{width:'70px'}}>Time(s)</th>
                    <th style={{width:'44px'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row, idx) => {
                    const isInvalid = !!describeCsvRowValidationError(row);
                    return (
                      <tr key={idx} className={isInvalid ? 'csv-row-invalid' : ''}>
                        <td className="csv-cell-num">{idx + 1}</td>
                        <td>
                          <textarea
                            className="csv-cell-input csv-cell-textarea"
                            value={row.question_text}
                            rows={2}
                            onChange={e => updateCsvRow(idx, 'question_text', e.target.value)}
                          />
                        </td>
                        {['option_a','option_b','option_c','option_d'].map(opt => (
                          <td key={opt}>
                            <input
                              className="csv-cell-input"
                              value={row[opt]}
                              onChange={e => updateCsvRow(idx, opt, e.target.value)}
                            />
                          </td>
                        ))}
                        <td>
                          <select
                            className="csv-cell-select"
                            value={row.correct_option}
                            onChange={e => updateCsvRow(idx, 'correct_option', e.target.value)}
                          >
                            <option value="">—</option>
                            {['A','B','C','D'].map(o => <option key={o}>{o}</option>)}
                          </select>
                        </td>
                        <td>
                          <select
                            className="csv-cell-select"
                            value={row.difficulty}
                            onChange={e => updateCsvRow(idx, 'difficulty', e.target.value)}
                          >
                            {['easy','medium','hard'].map(d => <option key={d}>{d}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            className="csv-cell-input"
                            value={row.category}
                            placeholder="—"
                            onChange={e => updateCsvRow(idx, 'category', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="1" max="10"
                            className="csv-cell-input csv-cell-num-input"
                            value={row.marks}
                            onChange={e => updateCsvRow(idx, 'marks', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="10" max="600"
                            className="csv-cell-input csv-cell-num-input"
                            value={row.time_limit}
                            onChange={e => updateCsvRow(idx, 'time_limit', parseInt(e.target.value) || 60)}
                          />
                        </td>
                        <td>
                          <button className="csv-row-del-btn" title="Remove row" onClick={() => removeCsvRow(idx)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="csv-save-footer">
              <button className="btn btn-outline" onClick={() => setView('list')}>Cancel</button>
              <button className="btn btn-primary" disabled={isBusy('csv-save')} onClick={handleBulkSave}>
                {isBusy('csv-save') ? <><span className="btn-spinner"/>Saving…</> : `Import ${csvRows.length} Question${csvRows.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     EDITOR VIEW
  ══════════════════════════════════════════════════════ */
  if (view === 'editor') {
    return (
      <div className="cq-container fade-up">

        {/* ── Top bar ── */}
        <div className="cq-topbar">
          <div className="cq-breadcrumb">
            <button className="cq-breadcrumb-link" onClick={backToList}>Question Bank</button>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            <span className="cq-breadcrumb-cur">{qEditId ? 'Edit Question' : 'Add New Question'}</span>
          </div>
          <div className="cq-header-row">
            <h2 className="cq-title">Curate Question</h2>
            <div className="cq-header-actions">
              <button className="btn btn-outline cq-draft-btn" disabled={isBusy('save-q')} onClick={() => handleSave(true)}>
                Save Draft
              </button>
              <button className="btn btn-primary cq-publish-btn" disabled={isBusy('save-q')} onClick={() => handleSave(false)}>
                {isBusy('save-q') ? <><span className="btn-spinner"/>Saving…</> : 'Publish'}
              </button>
            </div>
          </div>
        </div>

        {/* ── 2-column layout ── */}
        <div className="cq-layout">

          {/* ── LEFT: editor ── */}
          <div className="cq-left">

            {/* Formatting toolbar */}
            <div className="cq-toolbar">
              {[
                { title: 'Bold',    icon: <strong style={{fontFamily:'Georgia,serif',fontSize:'0.88rem'}}>B</strong> },
                { title: 'Italic',  icon: <em     style={{fontFamily:'Georgia,serif',fontSize:'0.88rem'}}>I</em> },
                { title: 'List',    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
                { title: 'Formula', icon: <span style={{fontFamily:'Georgia,serif',fontSize:'1rem',fontWeight:500}}>Σ</span> },
                { title: 'Image',   icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> },
                { title: 'Link',    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> },
              ].map(({ title, icon }) => (
                <button key={title} className="cq-tool-btn" title={title} type="button">{icon}</button>
              ))}
            </div>

            {/* Question prompt */}
            <div className="cq-editor-card">
              <div className="cq-section-label">Question Prompt</div>
              <textarea
                className="cq-question-textarea"
                placeholder="Type your intelligence-based question here..."
                value={qForm.question_text}
                onChange={e => setQForm(f => ({ ...f, question_text: e.target.value }))}
                rows={5}
              />
              <div className="cq-q-img-row">
                <label className="cq-add-img-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  {qForm.question_image ? 'Change image' : 'Add image'}
                  <input type="file" accept="image/*" style={{display:'none'}} onChange={e => uploadQuestionImage(e.target.files[0], 'question_image')}/>
                </label>
                {qForm.question_image && (
                  <div className="cq-q-img-preview">
                    <img src={`${IMG_BASE}${qForm.question_image}`} alt="Question"/>
                    <button type="button" className="cq-img-remove" onClick={() => setQForm(f => ({ ...f, question_image: '' }))}>✕</button>
                  </div>
                )}
              </div>
            </div>

            {/* Answer options */}
            <div className="cq-options-section">
              <div className="cq-options-header">
                <div className="cq-options-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Answer Options
                </div>
                <button type="button" className="cq-add-choice-btn">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Choice
                </button>
              </div>

              <div className="cq-options-grid">
                {OPTION_LETTERS.map(opt => {
                  const key    = `option_${opt.toLowerCase()}`;
                  const imgKey = `option_${opt.toLowerCase()}_image`;
                  const isCorrect = qForm.correct_option === opt;
                  return (
                    <div key={opt} className={`cq-option-card${isCorrect ? ' cq-option-correct' : ''}`}>
                      <div className="cq-option-header">
                        <span className="cq-option-badge" style={{ background: OPTION_COLORS[opt] }}>{opt}</span>
                        <div className="cq-option-correct-toggle" onClick={() => setQForm(f => ({ ...f, correct_option: opt }))}>
                          <span className="cq-correct-label">CORRECT</span>
                          <div className={`cq-correct-dot${isCorrect ? ' cq-correct-dot-on' : ''}`}/>
                        </div>
                      </div>
                      <textarea
                        className="cq-option-textarea"
                        placeholder="Enter option text..."
                        value={qForm[key]}
                        onChange={e => setQForm(f => ({ ...f, [key]: e.target.value }))}
                        rows={3}
                      />
                      {qForm[imgKey] ? (
                        <div className="cq-opt-img-wrap">
                          <img src={`${IMG_BASE}${qForm[imgKey]}`} alt={`Option ${opt}`} className="cq-opt-img"/>
                          <label className="cq-img-change-label">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Change image
                            <input type="file" accept="image/*" style={{display:'none'}} onChange={e => uploadQuestionImage(e.target.files[0], imgKey)}/>
                          </label>
                        </div>
                      ) : (
                        <label className="cq-add-img-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          Add image
                          <input type="file" accept="image/*" style={{display:'none'}} onChange={e => uploadQuestionImage(e.target.files[0], imgKey)}/>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── RIGHT: metadata + preview ── */}
          <div className="cq-right">

            {/* Metadata card */}
            <div className="cq-meta-card">
              <div className="cq-meta-title">Metadata &amp; Settings</div>

              <div className="cq-meta-field">
                <label className="cq-meta-label">Category</label>
                <div className="cq-select-wrap">
                  <select
                    className="cq-meta-select"
                    value={qForm.category}
                    onChange={e => setQForm(f => ({ ...f, category: e.target.value }))}
                  >
                    <option value="">Select category...</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <svg className="cq-select-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <div className="cq-meta-field">
                <label className="cq-meta-label">Difficulty Level</label>
                <div className="cq-difficulty-pills">
                  {['easy', 'medium', 'hard'].map(d => (
                    <button
                      key={d} type="button"
                      className={`cq-diff-pill${qForm.difficulty === d ? ` cq-diff-pill-${d}` : ''}`}
                      onClick={() => setQForm(f => ({ ...f, difficulty: d }))}
                    >
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cq-meta-row-2">
                <div className="cq-meta-field" style={{marginBottom:0}}>
                  <label className="cq-meta-label">Marks</label>
                  <input
                    type="number" min="1" max="10" className="cq-meta-input"
                    value={qForm.marks}
                    onChange={e => setQForm(f => ({ ...f, marks: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div className="cq-meta-field" style={{marginBottom:0}}>
                  <label className="cq-meta-label">Time Limit (s)</label>
                  <input
                    type="number" min="10" max="600" className="cq-meta-input"
                    value={qForm.time_limit || 60}
                    onChange={e => setQForm(f => ({ ...f, time_limit: parseInt(e.target.value) || 60 }))}
                  />
                </div>
              </div>

              <div className="cq-meta-field" style={{marginTop:'1rem', marginBottom:0}}>
                <label className="cq-meta-label">Tags</label>
                <div className="cq-tags-wrap">
                  {tags.map(t => (
                    <span key={t} className="cq-tag">
                      {t}
                      <button type="button" className="cq-tag-remove" onClick={() => setTags(p => p.filter(x => x !== t))}>×</button>
                    </span>
                  ))}
                  <input
                    className="cq-tag-input"
                    placeholder="Add..."
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }}}
                    onBlur={addTag}
                  />
                </div>
              </div>
            </div>

            {/* Question preview */}
            <div className="cq-preview-card">
              <div className="cq-meta-title">Question Preview</div>
              <div className="cq-preview-body">
                {qForm.question_image
                  ? <img src={`${IMG_BASE}${qForm.question_image}`} alt="Preview" className="cq-preview-img"/>
                  : <div className="cq-preview-placeholder">
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    </div>
                }
                {qForm.question_text && <p className="cq-preview-text">{qForm.question_text}</p>}
              </div>
            </div>

            {/* Status + ID */}
            <div className="cq-status-card">
              <div className="cq-status-row">
                <span className="cq-active-dot"/>
                <span className="cq-status-text">Active Status</span>
                {qEditId && <span className="cq-id-badge">ID: Q-{qEditId}</span>}
              </div>
              <p className="cq-autosave-note">Your changes are automatically saved as a local draft every 30 seconds.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     LIST VIEW
  ══════════════════════════════════════════════════════ */
  return (
    <div className="fade-up">
      {qStats && (
        <div className="q-stats-row">
          {[{l:'Total',v:qStats.total},{l:'Active',v:qStats.active,c:'green'},{l:'Inactive',v:qStats.inactive,c:'red'},{l:'Easy',v:qStats.easy},{l:'Medium',v:qStats.medium},{l:'Hard',v:qStats.hard,c:'gold'}].map(s => (
            <div key={s.l} className="q-stat-chip">
              <span style={{color:s.c?`var(--${s.c})`:'var(--text)'}}>{s.v}</span>{s.l}
            </div>
          ))}
        </div>
      )}

      <div className="q-list-toolbar">
        <div className="q-filters" style={{flex:1,marginBottom:0}}>
          <select className="input-field" style={{width:'auto'}} value={qFilter.category} onChange={e=>setQFilter(f=>({...f,category:e.target.value}))}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="input-field" style={{width:'auto'}} value={qFilter.difficulty} onChange={e=>setQFilter(f=>({...f,difficulty:e.target.value}))}>
            <option value="">All Difficulties</option>
            {['easy','medium','hard'].map(d => <option key={d}>{d}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={load}>Filter</button>
        </div>
        {can('questions','edit') && (
          <button className="btn btn-primary btn-sm cq-add-new-btn" onClick={() => openEditor()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add New Question
          </button>
        )}
        {can('questions','edit') && (
          <button className="btn btn-outline btn-sm cq-add-new-btn" onClick={openCSVImport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Import CSV
          </button>
        )}
      </div>

      {loading
        ? <div className="admin-loading"><div className="spinner"/></div>
        : questions.length === 0
          ? <div className="empty-state"><span>❓</span><p>No questions yet.</p></div>
          : (() => {
              const totalPages      = Math.ceil(questions.length / PAGE_SIZE);
              const pageStart       = (currentPage - 1) * PAGE_SIZE;
              const pageSlice       = questions.slice(pageStart, pageStart + PAGE_SIZE);
              const safeCurrentPage = Math.min(currentPage, totalPages);

              const getPageNumbers = () => {
                const pages = [];
                for (let p = 1; p <= totalPages; p++) {
                  if (p === 1 || p === totalPages || Math.abs(p - safeCurrentPage) <= 1) {
                    pages.push(p);
                  } else if (pages[pages.length - 1] !== '…') {
                    pages.push('…');
                  }
                }
                return pages;
              };

              return (
                <>
                  <div className="results-table-wrap">
                    <table className="results-table">
                      <thead>
                        <tr><th>#</th><th>Question</th><th>Category</th><th>Difficulty</th><th>Marks</th><th>Correct</th><th>Status</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {pageSlice.map((q, i) => (
                          <tr key={q.id}>
                            <td><code style={{color:'var(--muted)',fontSize:'0.75rem'}}>{pageStart + i + 1}</code></td>
                            <td style={{maxWidth:'280px'}}>
                              <div style={{fontSize:'0.85rem',color:'var(--text)',lineHeight:1.4}}>{q.question_text.length>80?q.question_text.slice(0,80)+'…':q.question_text}</div>
                              {q.question_image && <span style={{fontSize:'0.7rem',color:'var(--accent)',marginTop:'2px',display:'inline-block'}}>🖼 Has image</span>}
                            </td>
                            <td>{q.category?<span className="badge badge-purple">{q.category}</span>:'—'}</td>
                            <td><span className={`badge ${q.difficulty==='easy'?'badge-green':q.difficulty==='hard'?'badge-red':'badge-gold'}`}>{q.difficulty}</span></td>
                            <td><code>{q.marks}</code></td>
                            <td><span className="badge badge-green">{q.correct_option}</span></td>
                            <td><span className={`badge ${q.is_active?'badge-green':'badge-red'}`}>{q.is_active?'Active':'Inactive'}</span></td>
                            <td>
                              <div className="action-btns">
                                {can('questions','edit') && (
                                  <button className="act-btn info" title="Edit" onClick={() => openEditor(q)}><ActIcon type="edit"/></button>
                                )}
                                {can('questions','edit') && (
                                  <button className="act-btn warn" title={q.is_active?'Deactivate':'Activate'} disabled={isBusy(`qtgl-${q.id}`)} onClick={() => handleToggle(q.id, q.is_active)}>
                                    {isBusy(`qtgl-${q.id}`) ? <span className="act-spinner"/> : <ActIcon type={q.is_active?'lock':'unlock'}/>}
                                  </button>
                                )}
                                {can('questions','delete') && (
                                  <button className="act-btn danger" title="Delete" disabled={isBusy(`qdel-${q.id}`)} onClick={() => handleDelete(q.id)}>
                                    {isBusy(`qdel-${q.id}`) ? <span className="act-spinner"/> : <ActIcon type="trash"/>}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="q-pagination">
                      <span className="q-pagination__info">
                        {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, questions.length)} of {questions.length}
                      </span>
                      <div className="q-pagination__controls">
                        <button
                          className="q-pagination__btn"
                          disabled={safeCurrentPage === 1}
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>

                        {getPageNumbers().map((p, idx) =>
                          p === '…'
                            ? <span key={`ellipsis-${idx}`} className="q-pagination__ellipsis">…</span>
                            : <button
                                key={p}
                                className={`q-pagination__btn${safeCurrentPage === p ? ' q-pagination__btn--active' : ''}`}
                                onClick={() => setCurrentPage(p)}
                              >{p}</button>
                        )}

                        <button
                          className="q-pagination__btn"
                          disabled={safeCurrentPage === totalPages}
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          aria-label="Next page"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()
      }
    </div>
  );
}
